/**
 * YourSubscriptionPage.js
 * Shows the logged-in user's current active yearly magazine subscription status.
 * If no active subscription exists, prompts the user to subscribe.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, XCircle, Clock, Calendar, MapPin, Hash } from 'lucide-react';
import { vendorService } from '../../services/api';

const YourSubscriptionPage = () => {
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getMyMagazineSubscription();
      if (res.success && res.data) {
        setHasSubscription(res.data.has_subscription);
        setSubscription(res.data.subscription);
        setOrders(res.data.orders || []);
      } else {
        setError(res.error || 'Failed to load subscription details.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'COMPLETED': return { label: 'Active', color: 'bg-emerald-100 text-emerald-700 border border-emerald-200', icon: <CheckCircle size={15} /> };
      case 'EXPIRED':   return { label: 'Expired', color: 'bg-rose-100 text-rose-700 border border-rose-200', icon: <XCircle size={15} /> };
      case 'PENDING':   return { label: 'Pending Verification', color: 'bg-amber-100 text-amber-700 border border-amber-200', icon: <Clock size={15} /> };
      case 'FAILED':    return { label: 'Failed', color: 'bg-red-100 text-red-700 border border-red-200', icon: <XCircle size={15} /> };
      default:          return { label: status, color: 'bg-gray-100 text-gray-600 border border-gray-200', icon: null };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 text-sm max-w-xl mx-auto shadow-sm my-8 flex items-center gap-3">
        <XCircle className="text-red-500 flex-shrink-0" size={20} />
        <div>
          <h4 className="font-bold">Error Loading Subscription</h4>
          <p className="text-xs text-red-600/90 mt-0.5">{error}</p>
        </div>
      </div>
    );
  }

  // No subscription or orders at all
  if ((!hasSubscription || !subscription) && orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <div className="w-20 h-20 bg-[#e0f7f8] rounded-full flex items-center justify-center mb-5 animate-pulse">
          <BookOpen size={36} className="text-[#2ab391]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Active Subscription or Orders</h2>
        <p className="text-gray-500 mb-6 max-w-sm text-sm leading-relaxed">
          You don't have any active TII Magazine subscription or print copy orders yet.
          Subscribe now to get print editions delivered to your doorstep.
        </p>
        <Link
          to="/my-account/magazine/subscribe"
          className="inline-flex items-center gap-2 bg-[#2ab391] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#239c7e] transition-colors shadow-md hover:shadow-lg active:scale-95 duration-150"
        >
          <BookOpen size={16} />
          Subscribe / Order Now
        </Link>
      </div>
    );
  }

  const subscriptionStatus = subscription ? getStatusConfig(subscription.status) : null;

  return (
    <div className="px-2 sm:px-4 py-2 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <BookOpen size={24} className="text-[#2ab391]" />
          Magazine Subscriptions & Orders
        </h1>
        <p className="text-gray-500 text-sm mt-1">Manage your annual print subscriptions and recent edition orders</p>
      </div>

      {/* 1. Annual Subscription Card */}
      {hasSubscription && subscription && (
        <div className="bg-gradient-to-br from-[#f0fdf9] to-white border border-[#c0eee4] rounded-2xl p-4 sm:p-6 shadow-sm max-w-2xl transition-all hover:shadow-md">
          {/* Teal accent bar */}
          <div className="w-10 h-1 bg-[#2ab391] rounded-full mb-5" />

          {/* Plan badge */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">TII Magazine — Annual Plan</h2>
              <p className="text-gray-500 text-xs sm:text-sm mt-0.5">Yearly subscription — 4 Print Editions</p>
            </div>
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${subscriptionStatus.color}`}>
              {subscriptionStatus.icon}
              {subscriptionStatus.label}
            </span>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:border-[#2ab391]/20 transition-all">
              <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wider">Amount Paid</p>
              <p className="text-2xl font-bold text-[#2ab391]">₹{subscription.amount?.toLocaleString('en-IN')}</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:border-[#2ab391]/20 transition-all">
              <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wider flex items-center gap-1">
                <Calendar size={12} /> Start Date
              </p>
              <p className="text-base font-semibold text-gray-800">{formatDate(subscription.start_date)}</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:border-[#2ab391]/20 transition-all">
              <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wider flex items-center gap-1">
                <Calendar size={12} /> Valid Until
              </p>
              <p className="text-base font-semibold text-gray-800">{formatDate(subscription.end_date)}</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:border-[#2ab391]/20 transition-all">
              <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wider flex items-center gap-1">
                <Hash size={12} /> Order ID
              </p>
              <p className="text-xs font-mono text-gray-700 break-all select-all">{subscription.razorpay_order_id}</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:border-[#2ab391]/20 transition-all sm:col-span-2">
              <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wider flex items-center gap-1">
                <MapPin size={12} /> Delivery Address
              </p>
              <p className="text-sm text-gray-700 leading-relaxed font-medium">{subscription.delivery_address}</p>
              <p className="text-xs text-gray-500 mt-1 bg-gray-50 px-2.5 py-1 rounded inline-block">Pincode: {subscription.pincode}</p>
            </div>
          </div>

          {/* Features reminder */}
          <div className="mt-6 pt-5 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-2.5 font-medium uppercase tracking-wider">Subscription Includes</p>
            <div className="flex flex-wrap gap-2">
              {['4 Print Editions/Year', 'GST Included', 'Delivery Across India'].map((f) => (
                <span key={f} className="flex items-center gap-1 bg-[#e0f7f8] text-[#2ab391] text-xs font-semibold px-3 py-1.5 rounded-full">
                  ✓ {f}
                </span>
              ))}
            </div>
          </div>

          {/* Renew / CTA Section (For Expired or Failed Subscriptions) */}
          {subscription.status !== 'COMPLETED' && (
            <div className="mt-6 p-4 rounded-xl border border-dashed flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50 border-gray-300">
              <div className="text-center sm:text-left">
                <h4 className="text-sm font-semibold text-gray-800">
                  {subscription.status === 'PENDING' ? 'Verification in Progress' : 'Subscription Inactive'}
                </h4>
                <p className="text-xs text-gray-500 mt-1 max-w-sm">
                  {subscription.status === 'PENDING' 
                    ? 'We are verifying your transaction. If payment was made, your subscription will activate shortly.'
                    : 'To continue receiving the TII Print Magazine, please subscribe again.'}
                </p>
              </div>
              {subscription.status !== 'PENDING' && (
                <Link
                  to="/my-account/magazine/subscribe"
                  className="bg-[#2ab391] text-white hover:bg-[#239c7e] text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap shadow-sm hover:shadow active:scale-95 duration-100"
                >
                  Renew Subscription
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* 2. Magazine Print Copy Orders Section */}
      {orders.length > 0 && (
        <div className="max-w-2xl">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
            <BookOpen size={20} className="text-[#2ab391]" />
            Your Recent Print Copy Orders
          </h2>
          
          <div className="space-y-4">
            {orders.map((order) => {
              // Get payment status config
              const payStatus = getStatusConfig(order.status);
              
              // Get delivery status styling
              let deliveryBadgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
              if (order.delivery_status === 'Shipped') {
                deliveryBadgeClass = 'bg-blue-50 text-blue-700 border-blue-200';
              } else if (order.delivery_status === 'Delivered') {
                deliveryBadgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
              }
              
              return (
                <div key={order.id} className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-sm transition-all hover:shadow-md">
                  {/* Header row with edition and delivery badge */}
                  <div className="flex items-start justify-between flex-wrap gap-2 pb-3 border-b border-gray-100">
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm sm:text-base">
                        TII Magazine — {order.magazine_month} {order.magazine_year} Edition
                      </h3>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Ordered on {formatDate(order.created_at)}
                      </p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold border uppercase tracking-wider ${deliveryBadgeClass}`}>
                      Delivery: {order.delivery_status}
                    </span>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Quantity & Amount</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">
                        {order.quantity} copy/copies (₹{order.total_amount?.toLocaleString('en-IN')})
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Payment Status</p>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 mt-1 rounded-full text-xs font-semibold ${payStatus.color}`}>
                        {payStatus.icon}
                        {payStatus.label}
                      </span>
                    </div>

                    <div className="sm:col-span-2">
                      <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Shipping Details</p>
                      <p className="text-xs sm:text-sm text-gray-700 mt-1">
                        <strong>Recipient:</strong> {order.name} ({order.phone})
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mt-0.5">
                        <strong>Address:</strong> {order.delivery_address}, Pincode: {order.pincode}
                      </p>
                    </div>

                    <div className="sm:col-span-2 bg-gray-50/70 p-2.5 rounded-xl border border-gray-100">
                      <p className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider">Razorpay Order ID</p>
                      <p className="text-xs font-mono text-gray-600 break-all select-all mt-0.5">{order.razorpay_order_id}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default YourSubscriptionPage;
